export type ScreeningLabelKind = 'low' | 'risk' | 'unknown' | 'custom';

const normalize = (label: string | null | undefined) => (label ?? '').trim();

const normalizeLower = (label: string | null | undefined) => normalize(label).toLowerCase();

export function classifyScreeningLabel(label: string | null | undefined): ScreeningLabelKind {
  const raw = normalize(label);
  if (!raw) return 'unknown';

  const lower = raw.toLowerCase();

  // Explicit low-risk matches first (avoid matching generic '风险' within '低风险').
  if (
    lower === 'healthy' ||
    lower === 'normal' ||
    lower === 'low risk' ||
    lower === 'lower risk' ||
    raw.includes('低风险') ||
    raw.includes('风险较低') ||
    raw.includes('健康')
  )
    return 'low';

  if (
    lower === 'at risk' ||
    lower === 'risk' ||
    lower === 'high risk' ||
    lower === 'higher risk' ||
    raw.includes('疑似风险') ||
    raw.includes('高风险') ||
    raw.includes('风险较高') ||
    raw.includes('需要关注')
  )
    return 'risk';

  // Generic fallbacks (keep conservative).
  if (lower.includes('low') && lower.includes('risk')) return 'low';
  if (lower.includes('risk')) return 'risk';

  return 'custom';
}

export function isRiskLabel(label: string | null | undefined): boolean {
  return classifyScreeningLabel(label) === 'risk';
}

export function formatScreeningLabel(label: string | null | undefined): {
  text: string;
  color: 'success' | 'warning' | 'default';
  kind: ScreeningLabelKind;
} {
  const raw = normalize(label);
  const kind = classifyScreeningLabel(raw);

  if (kind === 'unknown') return { text: '未知', color: 'default', kind };
  if (kind === 'low') return { text: '低风险', color: 'success', kind };
  if (kind === 'risk') return { text: '疑似风险', color: 'warning', kind };

  // custom: show as-is
  return { text: raw, color: 'default', kind };
}

export function screeningRiskConfidence(
  label: string | null | undefined,
  confidence: number,
): number {
  const conf = Number.isFinite(confidence) ? confidence : 0;
  const clamped = Math.max(0, Math.min(1, conf));
  // When the label indicates risk, confidence is in the risk direction.
  // Otherwise, treat confidence as low-risk confidence and convert to risk.
  return isRiskLabel(label) ? clamped : 1 - clamped;
}

export function normalizeScreeningLabelRaw(label: string | null | undefined): string {
  return normalizeLower(label);
}
